import { Link } from '@tanstack/react-router'
import { ArrowUpRight, CalendarDays, RefreshCw, Star } from 'lucide-react'
import { useEvents } from '~/entities/event/model/use-events'
import { useActiveProfileId } from '~/entities/profile/model/use-profiles'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'
import type { Event } from '~/shared/types'
import { Badge } from '~/shared/ui/badge'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { Skeleton } from '~/shared/ui/skeleton'
import { eventDateParts, formatCount } from '../lib/overview-format'

const MAX_EVENTS = 4
const SKELETON_ROWS = ['first', 'second', 'third']

interface UpcomingEventsCardProps {
  businessId: string
  locale: string
  hiddenIds?: ReadonlySet<string>
  className?: string
}

export function UpcomingEventsCard({
  businessId,
  locale,
  hiddenIds,
  className,
}: UpcomingEventsCardProps) {
  const upcoming = useEvents(businessId, 'upcoming', useActiveProfileId(businessId))

  return (
    <Card className={cn('flex min-w-0 flex-col p-6 sm:p-7', className)}>
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="font-semibold font-serif text-foreground text-xl tracking-tight">
          {m.upcomingEvents()}
        </h2>
        <Link
          to="/dashboard/events"
          className="flex shrink-0 items-center gap-1 font-medium text-muted-foreground text-xs transition-colors hover:text-foreground"
        >
          {m.overviewAllEvents()}
          <ArrowUpRight className="size-3.5" />
        </Link>
      </div>
      <UpcomingEventsBody
        waiting={!businessId}
        upcoming={upcoming}
        hiddenIds={hiddenIds}
        locale={locale}
      />
    </Card>
  )
}

interface UpcomingEventsBodyProps {
  waiting: boolean
  upcoming: ReturnType<typeof useEvents>
  hiddenIds?: ReadonlySet<string>
  locale: string
}

function UpcomingEventsBody({ waiting, upcoming, hiddenIds, locale }: UpcomingEventsBodyProps) {
  if (waiting || upcoming.isLoading) {
    return (
      <ul className="mt-5 flex flex-col gap-4">
        {SKELETON_ROWS.map((row) => (
          <li key={row} className="flex items-center gap-4">
            <Skeleton className="h-12 w-11 shrink-0 rounded-lg" />
            <div className="flex flex-1 flex-col gap-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/3" />
            </div>
          </li>
        ))}
      </ul>
    )
  }

  if (upcoming.isError && upcoming.events.length === 0) {
    return (
      <div className="mt-5 flex flex-col items-start gap-3">
        <p className="text-destructive text-sm">{m.overviewEventsLoadError()}</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => upcoming.refetch()}
          disabled={upcoming.isFetching}
        >
          <RefreshCw className={cn(upcoming.isFetching && 'motion-safe:animate-spin')} />
          {m.tryAgain()}
        </Button>
      </div>
    )
  }

  const shown = upcoming.events
    .filter((event) => !event.isCanceled && !hiddenIds?.has(event.id))
    .slice(0, MAX_EVENTS)

  if (shown.length === 0) {
    return (
      <div className="mt-5 flex flex-col items-start gap-3 rounded-lg border border-border border-dashed p-5">
        <CalendarDays className="size-5 text-muted-foreground" />
        <p className="text-muted-foreground text-sm">{m.noUpcomingEvents()}</p>
        <Link
          to="/dashboard/events/create"
          className="font-medium text-brand-subtle-foreground text-sm hover:underline"
        >
          {m.createEvent()}
        </Link>
      </div>
    )
  }

  return (
    <ol className="mt-5 flex flex-col">
      {shown.map((event, index) => (
        <li
          key={event.id}
          className={cn(index !== shown.length - 1 && 'border-border/70 border-b')}
        >
          <UpcomingEventRow event={event} locale={locale} />
        </li>
      ))}
    </ol>
  )
}

function UpcomingEventRow({ event, locale }: { event: Event; locale: string }) {
  const { month, day, time } = eventDateParts(event.startTime, locale)
  const isDraft = event.isDraft || event.status === 'Draft'

  return (
    <Link
      to="/dashboard/events/$eventId"
      params={{ eventId: event.id }}
      className="-mx-2 flex items-center gap-4 rounded-lg px-2 py-3 transition-colors duration-150 ease-spring hover:bg-accent/60"
    >
      <span className="flex w-11 shrink-0 flex-col items-center rounded-lg border border-border bg-background py-1.5 leading-none">
        <span className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
          {month}
        </span>
        <span className="mt-0.5 font-semibold font-serif text-foreground text-lg tabular-nums">
          {day}
        </span>
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium text-foreground text-sm">{event.title}</span>
        <span className="mt-0.5 flex flex-wrap items-center gap-x-2 text-muted-foreground text-xs">
          <span className="tabular-nums">{time}</span>
          <span aria-hidden>·</span>
          <span className="inline-flex items-center gap-1" title={m.likes()}>
            <Star aria-hidden className="size-3" />
            <span className="tabular-nums">{formatCount(event.interestedCount, locale)}</span>
            <span className="sr-only">{m.likes()}</span>
          </span>
        </span>
      </span>
      {isDraft && (
        <Badge variant="secondary" className="shrink-0">
          {m.draft()}
        </Badge>
      )}
    </Link>
  )
}
